import * as signalR from '@microsoft/signalr';
import baseUrl from '../api/baseUrl.ts';

type EventHandler<TPayload = unknown> = (payload: TPayload) => void;

class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private eventHandlers: Map<string, EventHandler[]> = new Map();
    private isConnected = false;
    // Group membership is per-connectionId on the server, so it is LOST on
    // every reconnect. We remember what was joined and rejoin on reconnect;
    // resync handlers then let consumers refetch whatever was missed while
    // the connection was down.
    private joinedCustomerGroups = new Set<string>();
    private joinedPersonGroups = new Set<string>();
    private resyncHandlers: Array<() => void> = [];

    public async startConnection(
        accessTokenFactory?: () => Promise<string> | string
    ): Promise<void> {
        if (this.connection) {
            const state = this.connection.state;
            if (state !== signalR.HubConnectionState.Disconnected) {
                // Connected / connecting / reconnecting — nothing to do.
                return;
            }
            // Dead connection object: rebuild from scratch.
            this.connection = null;
        }

        const hubUrl = `${baseUrl}/hubs/resource-allocation`;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                // Attach the JWT token via factory
                accessTokenFactory: accessTokenFactory
                    ? accessTokenFactory
                    : undefined,
                withCredentials: true
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.connection.on(
            'ReceiveEvent',
            (eventData: {type: string; data: unknown}) => {
                const {type: eventType, data: payload} = eventData;
                console.log(`[SignalR] Received ${eventType}`, payload);
                this.trigger(eventType, payload);
                // Also trigger generic 'ReceiveEvent' for listeners that handle all events
                this.trigger('ReceiveEvent', eventData);
            }
        );

        this.connection.on('PersonContextChanged', (payload: unknown) => {
            console.log(`[SignalR] Received PersonContextChanged`, payload);
            this.trigger('PersonContextChanged', payload);
        });

        this.connection.onreconnecting(() => {
            this.isConnected = false;
            console.warn('[SignalR] Connection lost, reconnecting…');
        });

        this.connection.onreconnected(async () => {
            this.isConnected = true;
            console.log('[SignalR] Reconnected — rejoining groups');
            await this.rejoinGroups();
            // Let consumers catch up on whatever was broadcast while down.
            this.resyncHandlers.forEach(handler => handler());
        });

        this.connection.onclose(() => {
            this.isConnected = false;
            console.warn('[SignalR] Connection closed');
        });

        try {
            await this.connection.start();
            this.isConnected = true;
            console.log('[SignalR] Connected');
            // A fresh connectionId has no groups — re-establish any recorded
            // memberships (covers a rebuild after a dead connection).
            await this.rejoinGroups();
        } catch (err) {
            console.error('[SignalR] Connection failed', err);
        }
    }

    private async rejoinGroups(): Promise<void> {
        for (const customerId of this.joinedCustomerGroups) {
            try {
                await this.connection?.invoke('JoinGroup', customerId);
                console.log(`[SignalR] Rejoined group: ${customerId}`);
            } catch (err) {
                console.error(
                    `[SignalR] Failed to rejoin group: ${customerId}`,
                    err
                );
            }
        }
        for (const personId of this.joinedPersonGroups) {
            try {
                await this.connection?.invoke('JoinPersonGroup', personId);
                console.log(`[SignalR] Rejoined person group: ${personId}`);
            } catch (err) {
                console.error(
                    `[SignalR] Failed to rejoin person group: ${personId}`,
                    err
                );
            }
        }
    }

    // Called after a reconnect has re-established group membership — the
    // moment to refetch state that events may have been missed for.
    public onResync(handler: () => void): void {
        this.resyncHandlers.push(handler);
    }

    public offResync(handler: () => void): void {
        this.resyncHandlers = this.resyncHandlers.filter(h => h !== handler);
    }

    public async stopConnection(): Promise<void> {
        if (this.connection) {
            await this.connection.stop();
            this.isConnected = false;
            console.log('[SignalR] Disconnected');
        }
    }

    public async joinGroup(customerId: string): Promise<void> {
        this.joinedCustomerGroups.add(customerId);
        if (!this.connection) return;
        try {
            await this.connection.invoke('JoinGroup', customerId);
            console.log(`[SignalR] Joined group: ${customerId}`);
        } catch (err) {
            console.error(`[SignalR] Failed to join group: ${customerId}`, err);
        }
    }

    public async joinPersonGroup(personId: string): Promise<void> {
        this.joinedPersonGroups.add(personId);
        if (!this.connection) return;
        try {
            await this.connection.invoke('JoinPersonGroup', personId);
            console.log(`[SignalR] Joined person group: ${personId}`);
        } catch (err) {
            console.error(
                `[SignalR] Failed to join person group: ${personId}`,
                err
            );
        }
    }

    public async leavePersonGroup(personId: string): Promise<void> {
        this.joinedPersonGroups.delete(personId);
        if (!this.connection) return;
        try {
            await this.connection.invoke('LeavePersonGroup', personId);
            console.log(`[SignalR] Left person group: ${personId}`);
        } catch (err) {
            console.error(
                `[SignalR] Failed to leave person group: ${personId}`,
                err
            );
        }
    }

    public async relayEvent(
        customerId: string,
        eventType: string,
        payload: unknown
    ): Promise<void> {
        if (!this.connection) return;
        try {
            await this.connection.invoke('RelayEvent', customerId, {
                type: eventType,
                data: payload
            });
        } catch (err) {
            console.error(`[SignalR] Failed to relay event: ${eventType}`, err);
        }
    }

    public on<TPayload = unknown>(
        eventType: string,
        handler: EventHandler<TPayload>
    ): void {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, []);
        }
        this.eventHandlers.get(eventType)?.push(handler as EventHandler);
    }

    public off<TPayload = unknown>(
        eventType: string,
        handler: EventHandler<TPayload>
    ): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            this.eventHandlers.set(
                eventType,
                handlers.filter(h => h !== handler)
            );
        }
    }

    private trigger(eventType: string, payload: unknown): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.forEach(handler => handler(payload));
        }
    }

    public getConnected(): boolean {
        return this.isConnected;
    }

    public getConnectionId(): string | null {
        return this.connection?.connectionId || null;
    }
}

export const signalRService = new SignalRService();
